import styled, { keyframes } from "styled-components";

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-50px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const slideInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(50px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(40, 167, 69, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(40, 167, 69, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(40, 167, 69, 0);
  }
`;

const pulseRed = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(220, 53, 69, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(220, 53, 69, 0);
  }
`;

export const Container = styled.section`
  display: flex;
  flex-direction: column;
  width: 100%;
  /* min-height: 100vh; */
  height: 100%;
`;

export const ContactHeader = styled.div`
  background: var(--surface);
  padding-top: 20px;
  margin-top: 75px;
  text-align: center;
  /* border-bottom: 2px solid var(--accent-light); */
  padding-bottom: 30px;

  h1 {
    font-size: 48px;
    font-weight: 700;
    margin-bottom: 20px;
    text-transform: uppercase;
    letter-spacing: 2px;
    background: var(--gradient-primary);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;

    @media (max-width: 768px) {
      font-size: 36px;
    }
  }

  p {
    font-size: 16px;
    color: var(--text-muted);
    max-width: 600px;
    margin: 0 auto;
    line-height: 1.8;
  }

  @media (max-width: 920px) {
    margin-top: 66.7px;
  }
`;

export const ContactContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  /* background: var(--surface); */
  padding: 60px 40px;
  border-radius: 30px;
  max-width: 1400px;
  margin: 0 auto;
  margin-top: 40px;
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 40px;
    padding: 40px 20px;
  }
`;

export const ContactInfo = styled.div`
  animation: ${slideInLeft} 1s ease-out;

  h2 {
    font-size: 32px;
    font-weight: 700;
    margin-bottom: 30px;
    text-transform: uppercase;
    background: var(--gradient-primary);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;

    @media (max-width: 768px) {
      font-size: 24px;
    }
  }

  p {
    font-size: 16px;
    line-height: 1.8;
    color: var(--text-secondary);
    margin-bottom: 30px;
  }
`;

export const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 25px;
  padding: 20px;
  background: var(--surface);
  border-radius: 15px;
  border: 2px solid var(--accent-light);
  transition: all 0.3s ease;

  &:hover {
    /* border-color: var(--accent); */
    transform: translateX(5px);
    box-shadow: var(--shadow-soft);
  }

  .icon {
    width: 50px;
    height: 50px;
    background: var(--gradient-primary);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    color: var(--surface);
    flex-shrink: 0;
  }

  .info {
    flex: 1;

    h4 {
      font-size: 16px;
      font-weight: 600;
      color: var(--text-primary);
      margin-bottom: 5px;
      text-transform: uppercase;
    }

    p {
      color: var(--text-secondary);
      margin: 0;
      font-size: 14px;
    }

    a {
      color: var(--accent);
      text-decoration: none;
      transition: color 0.3s ease;

      &:hover {
        color: var(--accent-dark);
      }
    }

    .business-hours {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-top: 8px;
    }

    .status-indicator {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      flex-shrink: 0;

      &.open {
        background-color: var(--success);
        animation: ${pulse} 2s infinite;
      }

      &.closed {
        background-color: var(--error);
        animation: ${pulseRed} 2s infinite;
      }
    }

    .status-text {
      font-size: 12px;
      color: var(--text-muted);
      font-style: italic;
    }
  }
`;

export const ContactForm = styled.form`
  animation: ${slideInRight} 1s ease-out 0.3s both;

  h3 {
    font-size: 24px;
    font-weight: 600;
    margin-bottom: 25px;
    text-transform: uppercase;
    color: var(--text-primary);
  }
`;

export const FormGroup = styled.div`
  margin-bottom: 25px;

  label {
    display: block;
    font-size: 14px;
    font-weight: 600;
    color: var(--text-primary);
    margin-bottom: 8px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .required {
    color: var(--accent);
  }
`;

export const Input = styled.input`
  width: 100%;
  padding: 15px 20px;
  border: 2px solid var(--accent-light);
  border-radius: 15px;
  font-size: 16px;
  font-family: "Source Code Pro", monospace;
  background: var(--surface);
  color: var(--text-primary);
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: var(--accent);
    box-shadow: 0 0 0 3px rgba(124, 14, 51, 0.1);
  }

  &::placeholder {
    color: var(--text-muted);
  }
`;

export const TextArea = styled.textarea`
  width: 100%;
  padding: 15px 20px;
  border: 2px solid var(--accent-light);
  border-radius: 15px;
  font-size: 16px;
  font-family: "Source Code Pro", monospace;
  background: var(--surface);
  color: var(--text-primary);
  min-height: 120px;
  resize: vertical;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: var(--accent);
    box-shadow: 0 0 0 3px rgba(124, 14, 51, 0.1);
  }

  &::placeholder {
    color: var(--text-muted);
  }
`;

export const SubmitButton = styled.button`
  width: 100%;
  padding: 18px;
  background: var(--gradient-primary);
  color: var(--surface);
  border: none;
  border-radius: 15px;
  font-size: 16px;
  font-weight: 600;
  font-family: "Source Code Pro", monospace;
  text-transform: uppercase;
  letter-spacing: 1px;
  cursor: pointer;
  transition:
    color 0.3s ease,
    box-shadow 0.3s ease,
    transform 0.3s ease;

  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-top: 10px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-medium);
    background: var(--accent-dark);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

export const MapSection = styled.div`
  background: var(--surface);
  padding: 60px 40px;

  text-align: center;

  @media (max-width: 768px) {
    padding: 40px 20px;
  }

  h2 {
    font-size: 32px;
    font-weight: 700;
    margin-bottom: 20px;
    text-transform: uppercase;
    background: var(--gradient-primary);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;

    @media (max-width: 768px) {
      font-size: 24px;
    }
  }

  p {
    font-size: 16px;
    color: var(--text-secondary);
    margin-bottom: 30px;
    line-height: 1.6;
  }
`;

export const MapPlaceholder = styled.div`
  width: 100%;
  height: 300px;
  background: var(--background);
  border: 2px solid var(--accent-light);
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 15px;
  max-width: 1400px;
  margin: 0 auto;
  color: var(--text-muted);
  animation: ${scaleIn} 1s ease-out;
  transition: all 0.3s ease;

  &:hover {
    border-color: var(--accent);
  }

  .icon {
    font-size: 48px;
    color: var(--accent);
  }

  p {
    font-size: 16px;
    margin: 0;
  }
`;

export const WorkingHours = styled.div`
  background: var(--background);
  padding: 40px;
  border-radius: 30px;
  max-width: 1400px;
  display: flex;
  margin: 0 auto;
  flex-direction: column;
  width: 100%;

  @media (max-width: 768px) {
    padding: 30px 20px;
  }

  h2 {
    font-size: 28px;
    font-weight: 700;
    margin-bottom: 30px;
    text-transform: uppercase;
    background: var(--gradient-primary);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    text-align: center;

    @media (max-width: 768px) {
      font-size: 22px;
    }
  }
`;

export const HoursGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

export const HoursCard = styled.div`
  background: var(--surface);
  padding: 25px 30px;
  border-radius: 20px;

  border: 2px solid var(--accent-light);
  transition: all 0.3s ease;
  animation: ${fadeInUp} 0.6s ease forwards;
  animation-delay: ${(props) => props.$delay || 0}ms;
  opacity: 0;

  &:hover {
    border-color: var(--accent);
    transform: translateY(-5px);
    box-shadow: var(--shadow-soft);
  }

  h3 {
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 15px;
    text-transform: uppercase;
    color: var(--accent);
    text-align: center;
  }

  .schedule {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
    padding: 8px 0;
    border-bottom: 1px solid var(--accent-light);

    &:last-child {
      border-bottom: none;
      margin-bottom: 0;
    }

    .day {
      font-weight: 500;
      color: var(--text-primary);
    }

    .time {
      color: var(--text-secondary);
      font-size: 14px;
    }

    &.closed .time {
      color: var(--text-muted);
      font-style: italic;
    }
  }
`;

export const FAQCard = styled.a`
  display: flex;
  align-items: center;
  gap: 10px;
  background: var(--surface);
  padding: 12px 18px;
  border-radius: 12px;
  border: 1px solid var(--accent-light);
  text-decoration: none;
  color: var(--accent);
  transition: all 0.3s ease;
  margin-top: 20px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;

  svg:last-child {
    transform: translateX(-5px);
  }

  &:hover {
    transform: translateX(5px);
    box-shadow: var(--shadow-soft);

    svg:last-child {
      transition: all 0.6s ease;
      transform: translateX(5px);
    }
  }

  svg:first-child {
    font-size: 16px;
  }

  span {
    font-weight: 600;
  }

  svg:last-child {
    font-size: 12px;
    transition: transform 0.3s ease;
    margin-left: auto;
  }

  @media (max-width: 768px) {
    padding: 10px 15px;
    font-size: 13px;

    svg:first-child {
      font-size: 14px;
    }

    svg:last-child {
      font-size: 11px;
    }
  }
`;
